/**
 * The Driver Report's column names and what each one means: the tab's headers and their ⓘ
 * explanations (only where a column needs one), and the PDF's headers, from one list so they
 * never disagree.
 */
import { CLASS_LIMIT, CLEAR_GAP_S, DRIVER_LIMIT, Z_WINDOW_S } from "./driver-performance";

export type DriverColumnId = "driver" | "car" | "spread" | "avgLap" | "fastLap" | "stdDev" | "avgZ" | "clearAirPct" | "overtakes" | "shown" | "laps" | "track";

export interface DriverColumnText {
  /** The tab's header. */
  name: string;
  /** The PDF's header (short, upper case). */
  pdf: string;
  /** The ⓘ explanation: what it is, and how to read it. None for a column that explains itself. */
  tip?: string;
  /** For a column with no ⓘ: its hover (and press-and-hold) tip. */
  hint?: string;
}

export const DRIVER_COLUMNS: Record<DriverColumnId, DriverColumnText> = {
  driver: { name: "Driver", pdf: "DRIVER", hint: "The driver, as the timing names them. Tap to sort A to Z." },
  car: { name: "Car", pdf: "CAR", hint: "The car the driver drove, and its team. Tap to sort." },
  spread: { name: "Lap-time spread", pdf: "LAP-TIME SPREAD", hint: "Each counted lap as a tick, from the class's quickest (left) to slowest: a tight cluster is a consistent driver." },
  avgLap: { name: "Avg lap", pdf: "AVG LAP", hint: "Average of the driver's counted laps: the fastest racing laps, at most half the laps driven." },
  fastLap: { name: "Best lap", pdf: "BEST LAP", hint: "The driver's quickest lap." },
  stdDev: {
    name: "Consistency",
    pdf: "CONSISTENCY",
    tip: "How much the counted laps vary, in seconds (standard deviation). Lower is more consistent.",
  },
  avgZ: {
    name: "Pace vs class",
    pdf: "PACE VS CLASS",
    tip: `Each counted lap against the class's counted laps run within ${Math.round(Z_WINDOW_S / 60)} minutes of it, in spreads of the class (z-score). 0 is the class's pace at that time; below 0 is quicker. Fair between drivers who raced in different conditions.`,
  },
  clearAirPct: {
    name: "Clean-air laps",
    pdf: "CLEAN AIR",
    tip: `Share of the counted laps with no car within ${CLEAR_GAP_S} s ahead when the driver crossed the line at the start and at the end of the lap. Traffic between the lines can be missed.`,
  },
  overtakes: {
    name: "Passes",
    pdf: "PASSES",
    tip: "Cars of any class the driver passed on track: ahead of the driver at one crossing of the line, behind at the next. Pit and caution laps don't count.",
  },
  shown: {
    name: "Laps counted",
    pdf: "LAPS COUNTED",
    tip: `Laps in the average: the fastest racing laps, at most half the laps driven. Lap 1, pit in- and out-laps, caution laps, and laps over ${Math.round((CLASS_LIMIT - 1) * 100)}% off the class's best or ${Math.round((DRIVER_LIMIT - 1) * 100)}% off the driver's own are left out.`,
  },
  laps: { name: "Laps driven", pdf: "LAPS DRIVEN", tip: "Laps the driver drove, and their share of the car's laps." },
  track: {
    name: "Time on track",
    pdf: "TIME ON TRACK",
    tip: "The driver's stints from the start to the flag, lighter early and darker late, and the time at the wheel: on track, out of the pit lane, as the drive-time rules count it. With \"≈\" it is the driver's laps added up, pit laps included (a race without an analysis package). DNF marks where the car stopped.",
  },
};

/** A column's tip: its ⓘ explanation, else its hint. */
export const driverColumnTip = (id: DriverColumnId): string | undefined => DRIVER_COLUMNS[id].tip ?? DRIVER_COLUMNS[id].hint;
